<template></template>

<script lang="ts" setup>
import { TRACKING_IMPRESSION_ACTIONS } from '~~/layers/core/tracking/app/types/tracking'

import {
  GeoCheckStatus,
  LimitPeriod,
  type Money,
  PlayerCheckStatus,
  type Restrictions,
} from '#api/shared/@types/generated/backend/graphql-schema-types'
import {
  CheckoutLimitModal,
  CheckoutNeighborStateModal,
  CheckoutRestrictionsPlayerModal,
  ComplianceErrorsModal,
} from '#components'
import { ComplianceError } from '~/composables/compliance'

type CheckoutRestrictionsProps = {
  balance: Money
  closeAllModalsOnConfirm?: boolean
  grossTotal?: Money
  restrictions: Restrictions
}

const { balance, closeAllModalsOnConfirm, grossTotal, restrictions } =
  defineProps<CheckoutRestrictionsProps>()

const { registerModal } = useModal()

const errorVisible = ref({
  depositLimit: false,
  geoCheck: false,
  neighborState: false,
  playerRestrictions: false,
  spendingLimit: false,
})

const complianceError = ref<ComplianceError>(ComplianceError.GeoCheckGeneral)

const EMPTY_MONEY: Money = { currency: 'USD', value: 0 }

const checkRestrictions = () => {
  if (!restrictions) return

  if (restrictions.geoCheck !== GeoCheckStatus.Ok) {
    switch (restrictions.geoCheck) {
      case GeoCheckStatus.IpHostingFacilityDetected:
        errorVisible.value.geoCheck = true
        complianceError.value = ComplianceError.IpHostingFacilityDetected
        break
      case GeoCheckStatus.LocationSpoofingDetected:
        errorVisible.value.geoCheck = true
        complianceError.value = ComplianceError.LocationSpoofingDetected
        break
      case GeoCheckStatus.NeighbourState:
        errorVisible.value.neighborState = true
        break
      default:
        errorVisible.value.geoCheck = true
        complianceError.value = ComplianceError.GeoCheckGeneral
        break
    }
  }
  if (restrictions.playerCheck !== PlayerCheckStatus.Ok)
    errorVisible.value.playerRestrictions = true

  if (restrictions.spendingLimit) {
    errorVisible.value.spendingLimit = true
  }
  if (restrictions.depositLimit) {
    errorVisible.value.depositLimit = true
  }
}

onMounted(checkRestrictions)

const handleConfirm = (errorType: keyof (typeof errorVisible)['value']) => {
  errorVisible.value[errorType] = false

  if (closeAllModalsOnConfirm) {
    Object.keys(errorVisible.value).forEach((key) => {
      errorVisible.value[key as keyof typeof errorVisible.value] = false
    })
  }
}

registerModal({
  component: ComplianceErrorsModal,
  onConfirm: () => handleConfirm('geoCheck'),
  priority: 50,
  props: () => ({
    error: complianceError.value,
    trackingAction: TRACKING_IMPRESSION_ACTIONS.Checkout,
  }),
  when: () => errorVisible.value.geoCheck,
})

registerModal({
  component: CheckoutNeighborStateModal,
  onConfirm: () => handleConfirm('neighborState'),
  priority: 45,
  when: () => errorVisible.value.neighborState,
})

registerModal({
  component: CheckoutRestrictionsPlayerModal,
  priority: 40,
  props: () => ({ status: restrictions.playerCheck }),
  when: () => errorVisible.value.playerRestrictions,
})

registerModal({
  component: CheckoutLimitModal,
  onConfirm: () => handleConfirm('depositLimit'),
  priority: 35,
  props: () => ({
    availableAmount: restrictions.depositLimit?.available ?? EMPTY_MONEY,
    balance,
    grossTotal,
    limit: restrictions.depositLimit?.value ?? EMPTY_MONEY,
    period: restrictions.depositLimit?.period ?? LimitPeriod.Day,
    type: 'deposit' as const,
  }),
  when: () => errorVisible.value.depositLimit && Boolean(restrictions.depositLimit),
})

registerModal({
  component: CheckoutLimitModal,
  onConfirm: () => handleConfirm('spendingLimit'),
  priority: 30,
  props: () => ({
    availableAmount: restrictions.spendingLimit?.available ?? EMPTY_MONEY,
    grossTotal,
    limit: restrictions.spendingLimit?.value ?? EMPTY_MONEY,
    period: restrictions.spendingLimit?.period ?? LimitPeriod.Day,
    type: 'spending' as const,
  }),
  when: () => errorVisible.value.spendingLimit && Boolean(restrictions.spendingLimit),
})

watch(() => restrictions, checkRestrictions)
</script>
